import { createRoot } from "react-dom/client";

import { PrinterOutlined } from "@ant-design/icons";
import { message } from "antd";

import { buildApiUrl, fetchApi } from "@neu/libui";
import type { EntityFormConfig, FormActionButtonsContext } from "@neu/libui";

import { PapLanguageModal } from "./PapLanguageModal";
import { resolveBridgePdfOutcome } from "./patient_case_bridge_pdf";
import { resolvePapPdfOutcome } from "./patient_case_pap_pdf";

async function openMemberCasePapPdf(
	entityID: string | number,
	pdftype: number
): Promise<void> {
	const url = buildApiUrl(
		`/pdf/print/member-case?id=${String(entityID)}&pdftype=${String(pdftype)}`
	);
	const result = await fetchApi(url, { parseAs: "blob" });
	const objectURL = URL.createObjectURL(result.blob);
	window.open(objectURL, "_blank");
}

function promptPapLanguageThenPrint(entityID: string | number): void {
	const container = document.createElement("div");
	document.body.appendChild(container);
	const root = createRoot(container);

	const handleClose = () => {
		root.unmount();
		container.remove();
	};

	root.render(
		<PapLanguageModal
			open
			entityID={entityID}
			onClose={handleClose}
			onPrint={openMemberCasePapPdf}
		/>
	);
}

const PRINT_340B_PDF_TYPE = 34;

/** Print 340B is only relevant once the case stage has entered the Discount Drug Program flow. */
function isDiscountDrugProgramStage(stage: unknown): boolean {
	return typeof stage === "string" && stage.includes("Discount Drug Program");
}

function hasBridgeSubform(values: Record<string, unknown>): boolean {
	const bridge = values.subform_bridge;
	return Array.isArray(bridge) && bridge.length > 0;
}

export const patient_case: EntityFormConfig = {
	actionButtons: (ctx: FormActionButtonsContext) => ({
		printPap: {
			onClick: async ({ formHandle: fh }) => {
				if (ctx.entityID === null) return;

				const { medication_short_name, medical_retail } =
					fh.getValues();
				const outcome = resolvePapPdfOutcome(
					medication_short_name,
					medical_retail
				);

				if (outcome.kind === "none") {
					message.warning(
						"No PAP PDF is configured for this medication and channel. Check medication short name and Medical or Pharmacy."
					);
					return;
				}

				if (outcome.kind === "language") {
					promptPapLanguageThenPrint(ctx.entityID);
					return;
				}

				try {
					await openMemberCasePapPdf(ctx.entityID, outcome.pdftype);
				} catch (err: unknown) {
					message.error(
						err instanceof Error ? err.message : "Print failed"
					);
				}
			},
			isVisible: () => ctx.entityID !== null,
			icon: <PrinterOutlined />,
			label: "Print PAP",
			position: "center",
			type: "secondary",
		},
		print340B: {
			onClick: async () => {
				if (ctx.entityID === null) return;

				try {
					await openMemberCasePapPdf(
						ctx.entityID,
						PRINT_340B_PDF_TYPE
					);
				} catch (err: unknown) {
					message.error(
						err instanceof Error ? err.message : "Print failed"
					);
				}
			},
			isVisible: () =>
				ctx.entityID !== null &&
				isDiscountDrugProgramStage(ctx.formHandle.getValues().stage),
			icon: <PrinterOutlined />,
			label: "Print 340B",
			position: "center",
			type: "secondary",
		},
		printBridge: {
			onClick: async ({ formHandle: fh }) => {
				if (ctx.entityID === null) return;

				const { medication_short_name } = fh.getValues();
				const outcome = resolveBridgePdfOutcome(medication_short_name);

				if (outcome.kind === "none") {
					message.warning(
						"No Bridge Program PDF is configured for this medication. Currently supported: Bimzelx, Cosentyx, Kisqali."
					);
					return;
				}

				try {
					await openMemberCasePapPdf(ctx.entityID, outcome.pdftype);
				} catch (err: unknown) {
					message.error(
						err instanceof Error ? err.message : "Print failed"
					);
				}
			},
			isVisible: () =>
				ctx.entityID !== null &&
				hasBridgeSubform(
					ctx.formHandle.getValues() as Record<string, unknown>
				),
			icon: <PrinterOutlined />,
			label: "Print Bridge Program",
			position: "center",
			type: "secondary",
		},
	}),
};
